import { useEffect } from 'react';
import { useRouter } from '@abyss/web/hooks/useRouter';
import {
  acquireTokenSilently,
  hasMicrosoftAccount,
  initializeMsal,
  isInteractionRequiredError,
  loginWithMicrosoft,
  trySilentSingleSignOn,
} from '@src/api/common/MsalService';
import { UserService, type IUserData } from '@src/api/common/UserService';
import { AzureLoginHint } from '@src/constants/BaseUrls';

const buildInitials = (givenName: string, displayName: string): string => {
  if (givenName) {
    return givenName.slice(0, 2).toUpperCase();
  }

  return displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
};

const extractUser = (authResult: any): IUserData => {
  const claims = (authResult?.account?.idTokenClaims || {}) as Record<
    string,
    unknown
  >;
  const displayName =
    (typeof authResult?.account?.name === 'string' &&
      authResult.account.name) ||
    (typeof claims.name === 'string' && claims.name) ||
    'User';
  const givenName =
    (typeof claims.given_name === 'string' && claims.given_name) ||
    (typeof claims.name === 'string' && claims.name.split(' ')[0]) ||
    displayName;
  const emailAddress =
    (typeof claims.preferred_username === 'string' &&
      claims.preferred_username) ||
    (typeof claims.upn === 'string' && claims.upn) ||
    (typeof authResult?.account?.username === 'string' &&
      authResult.account.username) ||
    undefined;

  return {
    displayName,
    emailAddress,
    givenName,
    initials: buildInitials(givenName, displayName),
    userName: emailAddress,
  };
};

const SingleSignOnController = () => {
  const { navigate } = useRouter();
  const preferredLoginHint =
    UserService.GetLoginHint() || AzureLoginHint || null;

  const redirectToDashboard = () => {
    if (typeof window !== 'undefined') {
      window.location.replace('/');
      return;
    }

    navigate('/');
  };

  const persistSession = async (authResult: any) => {
    const user = extractUser(authResult);

    // authResult.accessToken from a login/SSO redirect is a Graph token
    // (aud: 00000003-0000-0000-c000-000000000000) which Flask will reject.
    // Explicitly acquire a token for our own API scope (aud: api://clientId or clientId).
    let accessToken: string | undefined;
    try {
      const apiTokenResult = await acquireTokenSilently(false);
      accessToken = apiTokenResult?.accessToken;
    } catch {
      // Silent API token acquisition failed — fall through to idToken fallback
    }

    // idToken aud = clientId — acceptable fallback so the session is still established
    if (!accessToken) {
      accessToken = authResult?.idToken;
    }

    UserService.SetUser({ user, accessToken });
  };

  const Action = {
    InitiateSSO: async () => {
      try {
        const redirectResult = await initializeMsal();

        if (redirectResult?.account) {
          await persistSession(redirectResult);
          redirectToDashboard();
          return;
        }

        if (hasMicrosoftAccount()) {
          const silentTokenResult = await acquireTokenSilently(true);

          if (silentTokenResult?.account) {
            await persistSession(silentTokenResult);
            redirectToDashboard();
            return;
          }
        }

        const seamlessResult = await trySilentSingleSignOn(preferredLoginHint);
        if (seamlessResult?.account) {
          await persistSession(seamlessResult);
          redirectToDashboard();
          return;
        }

        await loginWithMicrosoft(preferredLoginHint);
      } catch (error) {
        if (isInteractionRequiredError(error)) {
          await loginWithMicrosoft(preferredLoginHint);
          return;
        }

        // Keep the user on SSO flow and retry interactive login to avoid /login flash.
        console.error(
          'SSO flow hit a non-interaction error, retrying interactive login.',
          error
        );
        UserService.ClearSession();

        try {
          await loginWithMicrosoft(preferredLoginHint);
          return;
        } catch (redirectError) {
          console.error(
            'Unable to complete Microsoft sign-in redirect.',
            redirectError
          );
          navigate('/login');
        }
      }
    },
  };

  useEffect(() => {
    if (UserService.IsLoggedIn()) {
      redirectToDashboard();
      return;
    }

    UserService.ClearSession();
    Action.InitiateSSO();
  }, []);

  return { Action };
};

export { SingleSignOnController };
